import { useState, useCallback } from "react";
import { toast } from "sonner";

import { COLOUR_FALLBACK } from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";

export function useColorGroupEditing(
  customUpdateColor?: (
    groupName: string,
    originalColorName: string,
    name?: string,
    color?: string,
  ) => Promise<void>,
  customDeleteColor?: (groupName: string, colorName: string) => Promise<void>,
  customAddColor?: (
    groupName: string,
    colorName: string,
    colorValue: string,
  ) => Promise<void>,
  onGroupCreated?: (groupName: string) => void,
  customRenameGroup?: (oldName: string, newName: string) => Promise<void>,
  validateGroupName?: (
    groupName: string,
    excludeGroupName?: string,
  ) => { valid: boolean; error?: string },
  validateColorName?: (
    colorName: string,
    groupName?: string,
    excludeColorId?: string,
  ) => { valid: boolean; error?: string },
) {
  // Group editing state
  const [editingGroupName, setEditingGroupName] = useState<string | null>(null);
  const [tempGroupName, setTempGroupName] = useState("");
  const [isAddingNewGroup, setIsAddingNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // Color editing state
  const [addingColorToGroup, setAddingColorToGroup] = useState<string | null>(
    null,
  );
  const [newColorName, setNewColorName] = useState("");
  const [editingColorNameId, setEditingColorNameId] = useState<string | null>(
    null,
  );
  const [tempColorName, setTempColorName] = useState("");

  // Action methods are now required to be passed as parameters
  if (!customUpdateColor || !customDeleteColor || !customAddColor) {
    throw new Error(
      "useColorGroupEditing requires action methods to be passed as parameters",
    );
  }
  const updateColor = customUpdateColor;
  const deleteColor = customDeleteColor;
  const addColor = customAddColor;

  // Group editing functions
  const startEditingGroup = useCallback((groupName: string) => {
    setEditingGroupName(groupName);
    setTempGroupName(groupName);
  }, []);

  const cancelEditingGroup = useCallback(() => {
    setEditingGroupName(null);
    setTempGroupName("");
  }, []);

  const saveGroupName = useCallback(async () => {
    if (
      editingGroupName &&
      tempGroupName.trim() &&
      tempGroupName !== editingGroupName &&
      customRenameGroup
    ) {
      const newGroupName = tempGroupName.trim();

      // Use validation callback if provided
      if (validateGroupName) {
        const validation = validateGroupName(newGroupName, editingGroupName);
        if (!validation.valid) {
          toast.error("Invalid group name", {
            description: validation.error,
          });
          return;
        }
      }

      try {
        await customRenameGroup(editingGroupName, newGroupName);
      } catch (error) {
        console.error("Failed to rename group:", error);
        toast.error("Failed to rename group", {
          description:
            error instanceof Error ? error.message : "Unknown error occurred",
          duration: 5000,
        });
      }
    }
    setEditingGroupName(null);
    setTempGroupName("");
  }, [editingGroupName, tempGroupName, customRenameGroup, validateGroupName]);

  const handleGroupNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        saveGroupName();
      } else if (e.key === "Escape") {
        cancelEditingGroup();
      }
    },
    [saveGroupName, cancelEditingGroup],
  );

  // New group functions
  const startAddingNewGroup = useCallback(() => {
    setIsAddingNewGroup(true);
    setNewGroupName("New Group");
  }, []);

  const saveNewGroup = useCallback(async () => {
    if (!newGroupName.trim()) {
      cancelAddingNewGroup();
      return;
    }

    const trimmedGroupName = newGroupName.trim();

    // Use validation callback if provided
    if (validateGroupName) {
      const validation = validateGroupName(trimmedGroupName);
      if (!validation.valid) {
        if (validation.error?.includes("already exists")) {
          // Group already exists - just expand it and notify user
          toast.info(`Group "${trimmedGroupName}" already exists`, {
            description: "The existing group has been expanded for you.",
          });

          // Call onGroupCreated callback if provided (for expanding the group)
          if (onGroupCreated) {
            onGroupCreated(trimmedGroupName);
          }

          setIsAddingNewGroup(false);
          setNewGroupName("");
          return;
        } else {
          toast.error("Invalid group name", {
            description: validation.error,
          });
          return;
        }
      }
    }

    // Group doesn't exist - create a new group by adding a default color to it
    try {
      await addColor(trimmedGroupName, "default", COLOUR_FALLBACK);

      // Call onGroupCreated callback if provided
      if (onGroupCreated) {
        onGroupCreated(trimmedGroupName);
      }
    } catch (error) {
      console.error("Failed to add new group:", error);
      setIsAddingNewGroup(false);
      setNewGroupName("");
      return;
    }

    setIsAddingNewGroup(false);
    setNewGroupName("");
  }, [newGroupName, addColor, onGroupCreated, validateGroupName]);

  const cancelAddingNewGroup = useCallback(() => {
    setIsAddingNewGroup(false);
    setNewGroupName("");
  }, []);

  const handleNewGroupNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        saveNewGroup();
      } else if (e.key === "Escape") {
        cancelAddingNewGroup();
      }
    },
    [saveNewGroup, cancelAddingNewGroup],
  );

  // Color editing functions
  const startAddingColorToGroup = useCallback((groupName: string) => {
    setAddingColorToGroup(groupName);
    setNewColorName("");
  }, []);

  const saveNewColor = useCallback(async () => {
    if (!addingColorToGroup || !newColorName.trim()) {
      cancelAddingColor();
      return;
    }

    // Use validation callback if provided
    if (validateColorName) {
      const validation = validateColorName(
        newColorName.trim(),
        addingColorToGroup,
      );
      if (!validation.valid) {
        toast.error("Invalid color name", {
          description: validation.error,
        });
        return;
      }
    }

    try {
      const colorValue = COLOUR_FALLBACK;
      await addColor(addingColorToGroup, newColorName.trim(), colorValue);
      setAddingColorToGroup(null);
      setNewColorName("");
    } catch (error) {
      console.error("Failed to add color to group:", error);
    }
  }, [addingColorToGroup, newColorName, addColor, validateColorName]);

  const cancelAddingColor = useCallback(() => {
    setAddingColorToGroup(null);
    setNewColorName("");
  }, []);

  const handleNewColorNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        saveNewColor();
      } else if (e.key === "Escape") {
        cancelAddingColor();
      }
    },
    [saveNewColor, cancelAddingColor],
  );

  // Color name editing functions
  const startEditingColorName = useCallback(
    (colorPath: string, currentName: string) => {
      setEditingColorNameId(colorPath);
      setTempColorName(currentName);
    },
    [],
  );

  const cancelEditingColorName = useCallback(() => {
    setEditingColorNameId(null);
    setTempColorName("");
  }, []);

  const saveColorName = useCallback(async () => {
    if (editingColorNameId && tempColorName.trim()) {
      try {
        // Parse the path - handle both old dash format and new forward slash format
        const pathParts = editingColorNameId.split("/");
        let groupName: string;
        let originalColorName: string;

        if (pathParts.length >= 2) {
          // Grouped color: "groupName-colorName"
          groupName = pathParts[0];
          originalColorName = pathParts[pathParts.length - 1];
        } else {
          // Ungrouped color: "colorName" (group name is the same as color name)
          groupName = pathParts[0];
          originalColorName = pathParts[0];
        }

        // Skip network request if the name hasn't actually changed
        const newName = tempColorName.trim();
        if (newName === originalColorName) {
          setEditingColorNameId(null);
          setTempColorName("");
          return;
        }

        // Check if the new name contains a slash (group/color format)
        const parseColorName = (fullName: string) => {
          const parts = fullName.split("/");
          if (parts.length === 2) {
            return {
              groupName: parts[0].trim(),
              colorName: parts[1].trim(),
            };
          }
          return {
            groupName: null,
            colorName: fullName.trim(),
          };
        };

        const { groupName: newGroupName, colorName: newColorName } =
          parseColorName(tempColorName.trim());

        // Validation: Don't allow "/" in grouped color names (only allow for ungrouped → grouped conversion)
        if (pathParts.length >= 2 && newName.includes("/")) {
          console.warn('[VALIDATION] Cannot use "/" in grouped color names');
          toast.error('Cannot use "/" in grouped color names', {
            description: 'Use "/" only to convert ungrouped colors to groups.',
          });
          return;
        }

        // Use validation callback if provided
        if (validateColorName) {
          const targetGroupName =
            newGroupName || (pathParts.length >= 2 ? groupName : null);
          const excludeColorId =
            pathParts.length >= 2
              ? `${groupName}/${originalColorName}`
              : originalColorName;

          const validation = validateColorName(
            newName,
            targetGroupName || undefined,
            excludeColorId,
          );
          if (!validation.valid) {
            toast.error("Invalid color name", {
              description: validation.error,
            });
            return;
          }
        }

        // Check if this is a conversion from ungrouped to grouped (slash added)
        const isUngroupedToGrouped = newGroupName && pathParts.length === 1;
        const isCrossGroupMove = newGroupName && newGroupName !== groupName;

        await updateColor(
          groupName,
          originalColorName,
          tempColorName.trim(),
          undefined,
        );

        // If it's a cross-group move, expand the target group so user can see the moved color
        if ((isUngroupedToGrouped || isCrossGroupMove) && newGroupName) {
          onGroupCreated?.(newGroupName);
        }
      } catch (error) {
        console.error("Failed to update color name:", error);
      }
    }
    setEditingColorNameId(null);
    setTempColorName("");
  }, [
    editingColorNameId,
    tempColorName,
    updateColor,
    onGroupCreated,
    validateColorName,
  ]);

  const handleColorNameEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        saveColorName();
      } else if (e.key === "Escape") {
        cancelEditingColorName();
      }
    },
    [saveColorName, cancelEditingColorName],
  );

  return {
    // Group editing state
    editingGroupName,
    tempGroupName,
    isAddingNewGroup,
    newGroupName,

    // Color editing state
    addingColorToGroup,
    newColorName,
    editingColorNameId,
    tempColorName,

    // Group editing actions
    startEditingGroup,
    cancelEditingGroup,
    saveGroupName,
    setTempGroupName,
    handleGroupNameKeyDown,

    // New group actions
    startAddingNewGroup,
    saveNewGroup,
    cancelAddingNewGroup,
    setNewGroupName,
    handleNewGroupNameKeyDown,

    // Color editing actions
    startAddingColorToGroup,
    saveNewColor,
    cancelAddingColor,
    setNewColorName,
    handleNewColorNameKeyDown,

    // Color name editing actions
    startEditingColorName,
    cancelEditingColorName,
    saveColorName,
    setTempColorName,
    handleColorNameEditKeyDown,
  };
}
